"use client";

import { useState } from "react";
import AmountDisplay from "./amountDisplay";
import { PiArrowFatLinesRight } from "react-icons/pi";

const categories = [
    { id: "gold", label: "Gold" },
    { id: "coltan", label: "Coltan" },
    { id: "rare-earth", label: "Rare Earth" },
    { id: "tin", label: "Tin" },
] as const;

const data = {
    gold: {
        title: "Gold",
        tons: ["2,100,000", "OZ inferred"],
        grade: ["1.8–3.5 G/T.", "Grades Ranging"],
        description: [
            "14 km Gold Anomaly - 8 km core with soil samples up to 3 g/t Au in saprolitic shear zones.",
            "Structurally Controlled - Shear zones in saprolite/schist; geophysics link gold to sulfides.",
            "Pits & Trenches: 1.0–1.8 g/t Au in saprolite; up to 10 g/t Au near shears.",
            "Drilling: Intervals of 5–15 m at 2.0–3.5 g/t Au, localized highs to 7 g/t Au.",
            "Bulk Sampling & Trial Mining: 1.3–1.8 g/t Au average, up to 2.5 g/t Au near shear zones, 85–90% recovery.",
            "Economic Significance: Saprolitic deposits exceed typical open-pit cutoffs; free-milling gold reduces processing costs.",
        ],
        image: [
            "/exploration/gold1.svg",
            "/exploration/gold2.svg",
            "/exploration/gold3.svg",
        ],
    },
    coltan: {
        title: "Coltan (Tantalum and Niobium)",
        tons: ["190,000", "Tons inferred"],
        grade: ["28–40% TA₂O₅", "Grades Ranging"],
        description: [
            "Soil & Stream Sampling: Ta₂O₅: 150–300 ppm in soils; up to 350 ppm downstream.",
            "High-Grade Pegmatites: Rock assays: 18–28% Ta₂O₅.",
            "Geophysical Surveys: Identified corridor high-reflectivity & strong chargeability.",
            "Pitting & Trenching: Grades: 28–40% Ta₂O₅, 4–6% Nb₂O₅ over 3–8 m widths.",
            "Bulk Sampling & Trial Mining: 20–35% Ta₂O₅, 4–7% Nb₂O₅ in saprolitic/weathered pegmatites. Recovery ~85–90% via gravity separation; scalable production.",
            "7 km-long shear zone hosting Type 3 Pegmatites. Widths: 5–8 meters. Depth: 30–50 meters.",
            "Additional Minerals: Tin & REEs present; potential for by-product revenue.",
        ],
        image: [
            "/exploration/coltan1.svg",
            "/exploration/coltan2.svg",
            "/exploration/coltan3.svg",
        ],
    },
    "rare-earth": {
        title: "Rare Earth Elements (REES)",
        tons: ["9,000", "Tons"],
        grade: ["0.1% TREO", "Grades Ranging"],
        description: [
            "Exploration data highlights potential for rare earth deposits.",
            "Early geochemical indicators suggest viable concentrations of critical REEs like neodymium and cerium.",
            "Soil, stream, and pegmatite samples show strong light REE anomalies (300–600 ppm Ce, La, Nd), with up to 0.1% TREO in rock assays.",
            "Bulk sampling achieved 90–95% purity and 85–90% recovery, confirming solid economic potential.",
        ],
        image: [
            "/exploration/rare_earth1.svg",
            "/exploration/rare_earth2.svg",
            "/exploration/rare_earth3.svg",
        ],
    },
    tin: {
        title: "Tin",
        tons: ["18,000", "Tons"],
        grade: ["", "Grades Ranging"],
        description: [
            "Strongly associated with coltan mineralization in pegmatites, offering additional value streams.",
            "Tin appears in coltan-bearing pegmatites at Wilkinson Hill and Khatanta. IP/resistivity surveys identified tin-rich zones.",
            "Pits revealed 0.2–0.3% Sn, drilling reached 2.1% Sn in pegmatites, and bulk sampling/trial mining achieved 90–95% purity.",
        ],
        image: [
            "/exploration/tin1.svg",
            "/exploration/tin2.svg",
            "/exploration/tin3.svg",
        ],
    },
} as const;

type DataKey = keyof typeof data;

// No change in imports or data

export default function ExplorationHighlights() {
    const [selected, setSelected] = useState<DataKey>("gold");

    return (
        <div id="result_Highlights" className="w-full flex flex-col items-center justify-center p-4 sm:p-6">
            <div className="w-full  border-l-2 border-[#172B6B] px-2 text-[#172B6B] font-bold text-[22px] sm:text-[30px] font-bungeeinline mb-4 uppercase">
                Exploration RESULTS Highlights
            </div>
            <div className="w-full flex flex-col lg:flex-row justify-center items-center gap-4">
                <div className="w-full flex flex-col items-center justify-center p-4 sm:p-6 gap-4 border-2 border-[#172B6B] rounded-lg">
                    <div className="w-full flex flex-col lg:flex-row justify-center items-center gap-4">
                        <div className="w-full flex flex-col justify-center items-start">
                            <div className="w-full  font-normal text-[#253772] font-bungeeinline text-[18px] sm:text-[22px] lg:text-4xl py-4 sm:py-6 uppercase">
                                {data[selected].title}
                            </div>
                        </div>
                    </div>

                    {/* Selection Buttons */}
                    <div className="w-full flex flex-wrap gap-2 sm:gap-0">
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                className={`flex-1 text-center py-2 text-sm sm:text-base transition-colors duration-300 ${selected === cat.id
                                    ? "bg-gray-500 text-yellow-400 font-bold  border-[2px] border-yellow-400 border-b-[#253772] border-b-[4px] sm:border-b-[10px]"
                                    : "bg-gray-500 text-white border-yellow-200 border-b-4 border-x-[1px] border-x-white"
                                    }`}
                                onClick={() => setSelected(cat.id as DataKey)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    <div className="w-full flex flex-col sm:flex-row justify-start items-center gap-4">
                        <AmountDisplay value={data[selected].tons[0]} explanation={data[selected].tons[1]} />
                        <AmountDisplay value={data[selected].grade[0]} explanation={data[selected].grade[1]} />
                    </div>
                </div>

                <div className="w-full aspect-video flex flex-row justify-center items-center p-4">
                    <img src={data[selected].image[0]} alt="gold1" className="w-full h-full" />
                </div>
            </div>

            {/* Selected Data Display */}
            <div className="w-full max-w-7xl flex flex-col lg:flex-row justify-center items-center gap-6 p-4 sm:p-6">
                <div className="flex-1 mt-4 list-disc list-inside text-black text-lg sm:text-2xl font-normal rounded-lg shadow-lg p-4 sm:p-6">
                    {data[selected].description.map((desc, index) => (
                        <div className="w-full flex flex-row items-start gap-2" key={index}>
                      <img src="/exploration/right_arrow.png" alt="right_arrow" className="w-4 h-auto pt-3" />
                            {/* <PiArrowFatLinesRight className="pt-2 text-2xl"/> */}
                            <div className="w-full">{desc}</div>
                        </div>
                    ))}
                </div>
                <div className="w-full sm:w-[250px] flex flex-col gap-4">
                    <img src={data[selected].image[1]} alt="{data[selected].image[1]}" />
                    <img src={data[selected].image[2]} alt="{data[selected].image[2]}" />
                </div>
            </div>
        </div>
    );
}

